import React, { Fragment, useState, FC, useEffect } from 'react';
import { DateField, List, TextField, ListProps, ShowButton } from 'react-admin';
import Datagrid from 'components/Datagrid';
import { PreRockSheetFilter } from '../filters/pre-rock-sheet-filters';
import { makeStyles } from '@material-ui/core';
import { exporter } from 'providers/export';
import JobNotesModal from './job-notes-modal';
import { UIProvider } from 'providers/uiProvider';
import InfoIcon from '@material-ui/icons/Info';
import Box from '@material-ui/core/Box';
import CustomListActions from 'components/CustomListActions';
import { SupervisorField } from 'components/SupervisorListItems';
import { trackEvent } from 'utils/mixpanel';
import { PhaseStructureTypeEnum } from '@vatos-pas/common';
import { sanitizeHtml } from 'utils/sanitizeHtml';

const useStyles = makeStyles({
  content: {
    '@media(max-width: 1300px)': {
      overflow: 'auto',
    },
  },
  contentClosed: {
    '@media(max-width: 1300px)': {
      overflow: 'auto',
    },
  },
  root: {
    '@media(max-width: 1550px)': {
      width: 'calc(100vw - 260px)',
    },
    '@media(max-width: 850px)': {
      width: 'calc(100vw - 45px)',
      marginLeft: '15px',
    },
  },
  rootClosed: {
    '@media(max-width: 1550px)': {
      width: 'calc(100vw - 125px)',
    },
    '@media(max-width: 850px)': {
      width: 'calc(100vw - 45px)',
      marginLeft: '15px',
    },
  },
  link: {
    backgroundColor: '#e6005a',
    height: '50px',
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    display: 'flex',
  },
  linkText: {
    color: 'white',
  },
  pink: {
    backgroundColor: 'lightpink',
    cursor: 'pointer',
    textDecoration: 'underline',
  },
  normal: {
    cursor: 'pointer',
    backgroundColor: 'transparent',
    textDecoration: 'underline',
  },
  info: {
    cursor: 'pointer',
    marginRight: '10px',
  },
});

const NotesField = props => {
  const classes = useStyles(props);

  const lastJobNote =
    !!props.record.jobNotes?.length && props.record.jobNotes.at(-1);

  const cleanText = (() => {
    if (!lastJobNote?.note) return '';
    const el = document.createElement('div');
    el.innerHTML = sanitizeHtml(lastJobNote.note);
    return el.textContent || el.innerText || '';
  })();

  return (
    <Box height="30px" width="30vw" display="flex" alignItems="center">
      {cleanText && (
        <>
          <InfoIcon
            className={classes.info}
            onClick={() => props.setSelectedJob(props.record)}
          />
          <Box
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
            width="100%"
          >
            <span>{cleanText}</span>
          </Box>
        </>
      )}
    </Box>
  );
};

const CustomShow = props => {
  const { record } = props;

  const redirect = () => {
    const origin = window.location.href.split('#')[0];
    window.setTimeout(() => {
      if (record.jobId) {
        const route =
          record.phaseStructureType === PhaseStructureTypeEnum.DrywallAndPaint
            ? 'job-master-sheet-v2'
            : 'job-master-sheet';
        window.location.href = origin + '#/' + route + '/' + record.jobId;
      }
    }, 500);
  };

  return <ShowButton onClick={redirect} component={Box} />;
};

export const PreRockSheetList: FC<ListProps> = props => {
  const [openNotesModal, setOpenNotesModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState('');
  const { opened } = UIProvider.useContainer();
  const classes = useStyles(props);

  useEffect(() => {
    trackEvent('PageView', {
      resource: 'pre-rock',
      page: window.location.pathname,
      role: localStorage.getItem('userRoles'),
      env: process.env.REACT_APP_MIXPANEL_ENV,
    });
  }, []);
  return (
    <Fragment>
      <List
        classes={{
          content: opened ? classes.content : classes.contentClosed,
          root: opened ? classes.root : classes.rootClosed,
        }}
        {...props}
        exporter={exporter('pre-rock-sheet')}
        filters={<PreRockSheetFilter>{props.filters}</PreRockSheetFilter>}
        sort={{ field: 'createdAt', order: 'ASC' }}
        actions={<CustomListActions maxResults={999999} />}
        bulkActionButtons={<React.Fragment />}
      >
        <Datagrid>
          <DateField source="createdAt" label="Date Created" />
          <SupervisorField label="Supervisors" />
          <TextField source="jobLot" label="Lot" />
          <NotesField
            setSelectedJob={selected => {
              setSelectedJob(selected);
              setOpenNotesModal(true);
            }}
            source="notes"
            sortable={false}
            label="Notes"
          />
          <CustomShow />
        </Datagrid>
      </List>
      <JobNotesModal
        handleClose={() => setOpenNotesModal(false)}
        open={openNotesModal}
        selectedJob={selectedJob}
      />
    </Fragment>
  );
};
